// One person's access history — BR-37, from the access change log (BR-34's `AccessChangeLog`,
// append-only, kept for ever). Newest first; each entry says who changed it, when, and which
// assignments it added and took away. An assignment the change left as it was is neither.
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, Spinner } from "@mantis-sas/ui";

import { getUserAccessHistory } from "../../../ApiCalls/mantisAccess";
import { conNombresDePermiso } from "../../../utils/accesoDiccionario";
import { autorDeEntrada, cambiosDeEntrada, describirAsignacion, nombreDeUsuario } from "../../../utils/cierreAcceso";
import { fechaHoraBogota } from "../../../utils/cierrePanel";
import "./styles/brinksTheme.css";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

function Entrada({ entrada, roles, nombreDeCliente }) {
  const { agregadas, quitadas } = cambiosDeEntrada(entrada);
  return (
    <li className="space-y-1 rounded-[10px] border border-brinks-border bg-card p-3">
      <p className="text-[12px] text-brinks-muted-foreground">
        {fechaHoraBogota(entrada.dateCreated)} · {autorDeEntrada(entrada)}
      </p>
      {agregadas.length === 0 && quitadas.length === 0 ? (
        <p className="text-[12.5px] text-brinks-foreground">Sin cambios en las asignaciones.</p>
      ) : null}
      {agregadas.map((a) => (
        <p key={`+${describirAsignacion(a, roles, nombreDeCliente)}`} className="text-[12.5px] text-brinks-success">
          + {describirAsignacion(a, roles, nombreDeCliente)}
        </p>
      ))}
      {quitadas.map((a) => (
        <p key={`-${describirAsignacion(a, roles, nombreDeCliente)}`} className="text-[12.5px] text-brinks-danger">
          − {describirAsignacion(a, roles, nombreDeCliente)}
        </p>
      ))}
      {entrada.note ? <p className="text-[12px] text-brinks-muted-foreground">{entrada.note}</p> : null}
    </li>
  );
}

function Contenido({ token, usuario, roles, nombreDeCliente }) {
  const [entradas, setEntradas] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let activo = true;
    (async () => {
      const response = await getUserAccessHistory(token, usuario.id).catch(() => null);
      if (!activo) return;
      if (!response?.success) {
        setError(conNombresDePermiso(response?.message) || "No se pudo cargar el historial.");
        setEntradas([]);
        return;
      }
      setEntradas(response.data || []);
    })();
    return () => {
      activo = false;
    };
  }, [token, usuario.id]);

  if (entradas === null) {
    return (
      <div className="grid place-items-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="max-h-[65vh] space-y-2 overflow-y-auto p-4">
      {error ? (
        <p role="alert" className="rounded-[8px] border border-brinks-danger-border bg-brinks-danger-soft px-2.5 py-1.5 text-[12.5px] text-brinks-danger">
          {error}
        </p>
      ) : null}
      {!error && entradas.length === 0 ? (
        <p className="text-[12.5px] text-brinks-muted-foreground">Todavía no hay cambios registrados en el acceso de esta persona.</p>
      ) : null}
      <ol {...guia("Cambios", "Del más reciente al más antiguo: qué cambió, quién lo hizo y cuándo.")} className="space-y-2">
        {entradas.map((entrada) => (
          <Entrada key={entrada.id} entrada={entrada} roles={roles} nombreDeCliente={nombreDeCliente} />
        ))}
      </ol>
    </div>
  );
}

export function AccesoHistorial({ open, onOpenChange, token, usuario, roles, nombreDeCliente }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="brinks-scope font-redhat max-h-[90vh] max-w-xl gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b p-4 text-left">
          <div className="flex min-w-0 items-center gap-1">
            <DialogTitle className="text-[16px] font-semibold">
              Historial de acceso de {usuario ? nombreDeUsuario(usuario) : ""}
            </DialogTitle>
            <BotonGuia guiaId="dialogo-historial-acceso" titulo="Historial de acceso" texto="Cada cambio en el acceso de esta persona: qué rol se le dio o quitó, dónde, quién lo hizo y cuándo." nombre="Historial de acceso" />
          </div>
        </DialogHeader>
        {open && usuario ? (
          <Contenido token={token} usuario={usuario} roles={roles} nombreDeCliente={nombreDeCliente} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
